import type { CommendaAiSavedMessageFeedback } from '@commenda/api/commenda-ai/types';
import { Icons, Tooltip } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useEffect, useRef, useState } from 'react';
import { ChatIconButton } from './ChatIconButton';
import { CommendaAiMessageHelper } from './commenda-ai-message-helper';
import { MessageFeedbackActions } from './MessageFeedbackActions';

interface MessageActionsProps {
  /** Plain text of the message, used by the copy action. */
  text: string;
  /** When the message was stored. Rendered as its age beside the buttons. */
  sentAt?: string;
  /** Which side of the buttons the age sits on. The row hangs off the message's outer edge, so a
   * right-aligned user message reads leading and a left-aligned reply reads trailing. */
  sentAtPosition?: 'leading' | 'trailing';
  onRegenerate?: () => void;
  className?: string;
  /** Rating config for an assistant message. Omitted under user messages, which carry no thumbs. */
  feedbackConfig?: {
    conversationId: string;
    messageId: string;
    /** The verdict already saved on this message; keeps the thumb filled and prefills the modal. */
    initialFeedback: CommendaAiSavedMessageFeedback | null;
    onRatingSaved?: () => void;
  };
}

/** Message actions: copy, rate and (optionally) regenerate. Shown under a completed assistant
 * reply, and as a copy-only action under user messages. */
export function MessageActions({
  text,
  sentAt,
  sentAtPosition = 'leading',
  onRegenerate,
  className,
  feedbackConfig,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const sentAtLabel = (
    <span className="whitespace-nowrap txt-extra-small-regular text-text-tertiary">
      {CommendaAiMessageHelper.formatSentAt(sentAt)}
    </span>
  );

  return (
    <div className={cn('flex items-center gap-lg', className)}>
      {sentAtPosition === 'leading' && sentAtLabel}
      <div className="flex items-center gap-xs">
        <Tooltip content={copied ? 'Copied' : 'Copy'} asChild>
          <ChatIconButton
            aria-label={copied ? 'Copied' : 'Copy'}
            onClick={handleCopy}
            icon={
              copied ? (
                <Icons.Check className="size-4" aria-hidden="true" />
              ) : (
                <Icons.Copy06 className="size-4" aria-hidden="true" />
              )
            }
          />
        </Tooltip>
        {onRegenerate && (
          <Tooltip content="Try again" asChild>
            <ChatIconButton
              aria-label="Try again"
              onClick={onRegenerate}
              icon={
                <Icons.RefreshCcw04 className="size-4" aria-hidden="true" />
              }
            />
          </Tooltip>
        )}
        {feedbackConfig && (
          <MessageFeedbackActions
            conversationId={feedbackConfig.conversationId}
            messageId={feedbackConfig.messageId}
            initialFeedback={feedbackConfig.initialFeedback}
            onRatingSaved={feedbackConfig.onRatingSaved}
          />
        )}
      </div>
      {sentAtPosition === 'trailing' && sentAtLabel}
    </div>
  );
}
